// =============================================================================
// card-instance-service.ts — pure-function port of Cards/CardInstanceService.cs
// No class, no singleton, no module-level state.
// Monotonic InstanceId counter threaded through state (NOT module-level let).
// =============================================================================

import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// CardZone enum (ported from CardInstance.cs)
// ---------------------------------------------------------------------------

export const CardZone = {
  Hand: "Hand",
  Aura: "Aura",
  Discard: "Discard",
  Pending: "Pending",
} as const;
export type CardZone = (typeof CardZone)[keyof typeof CardZone];

// ---------------------------------------------------------------------------
// CardInstance (ported from CardInstance.cs struct — value-semantics via freeze)
// ---------------------------------------------------------------------------

export interface CardInstance {
  readonly instanceId: number;
  /** asset.name (SO filename without extension) — matches cards.json `name` field */
  readonly cardAssetId: string;
  readonly ownerSeat: number;
  readonly zone: CardZone;
}

// ---------------------------------------------------------------------------
// CardInstanceState — immutable, all mutation returns new state
// ---------------------------------------------------------------------------

export interface CardInstanceState {
  readonly nextId: number;
  /** keyed by stringified instanceId */
  readonly instances: Readonly<Record<string, CardInstance>>;
}

// ---------------------------------------------------------------------------
// Typed errors
// ---------------------------------------------------------------------------

export class CardInstanceError extends Error {
  constructor(
    message: string,
    public readonly code: "NullAsset" | "NotFound",
    public readonly instanceId?: number,
  ) {
    super(message);
    this.name = "CardInstanceError";
  }
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface CreateForSeatResult {
  readonly newState: CardInstanceState;
  readonly instanceId: number;
  readonly log: ActionLog;
}

export interface MoveZoneResult {
  readonly newState: CardInstanceState;
  readonly log: ActionLog;
}

export interface TransferOwnerResult {
  readonly newState: CardInstanceState;
  readonly log: ActionLog;
}

export interface DestroyResult {
  readonly newState: CardInstanceState;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createCardInstanceState(): CardInstanceState {
  return Object.freeze({ nextId: 1, instances: Object.freeze({}) });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Mint a new CardInstance for the given asset name + seat + zone.
 * `assetName` is the CardDef.name (SO filename) — never null.
 * Mirrors Unity CardInstanceService.CreateForSeat(asset, seat, zone).
 */
export function createForSeat(
  state: CardInstanceState,
  log: ActionLog,
  assetName: string,
  seat: number,
  zone: CardZone,
): CreateForSeatResult {
  if (!assetName) {
    console.error("[CardInstanceService] CreateForSeat called with empty assetName.");
    throw new CardInstanceError(
      "[CardInstanceService] createForSeat: assetName must be non-empty",
      "NullAsset",
    );
  }
  assertFiniteInt(seat, "createForSeat seat");

  const id = state.nextId;
  const inst: CardInstance = Object.freeze({
    instanceId: id,
    cardAssetId: assetName,
    ownerSeat: seat,
    zone,
  });
  const newInstances = Object.freeze({ ...state.instances, [id]: inst });
  const newState: CardInstanceState = Object.freeze({
    nextId: id + 1,
    instances: newInstances,
  });
  const nextLog = log.append({
    kind: "cardInstance.create",
    data: { instanceId: id, cardAssetId: assetName, seat, zone },
  });
  return { newState, instanceId: id, log: nextLog };
}

/**
 * Attempt to get a CardInstance by id.
 * Returns `{ ok: true, inst }` or `{ ok: false }`.
 * Mirrors Unity TryGet(id, out inst).
 */
export function tryGet(
  state: CardInstanceState,
  instanceId: number,
): { ok: true; inst: CardInstance } | { ok: false } {
  assertFiniteInt(instanceId, "tryGet instanceId");
  const inst = state.instances[instanceId];
  if (inst === undefined) return { ok: false };
  return { ok: true, inst };
}

/**
 * Move a CardInstance to a new zone. Logs error + throws if id not found.
 * Mirrors Unity MoveZone(id, newZone).
 */
export function moveZone(
  state: CardInstanceState,
  log: ActionLog,
  instanceId: number,
  newZone: CardZone,
): MoveZoneResult {
  assertFiniteInt(instanceId, "moveZone instanceId");
  const existing = state.instances[instanceId];
  if (existing === undefined) {
    console.error(`[CardInstanceService] MoveZone: id ${instanceId} not found.`);
    throw new CardInstanceError(
      `[CardInstanceService] moveZone: id ${instanceId} not found`,
      "NotFound",
      instanceId,
    );
  }
  const updated: CardInstance = Object.freeze({ ...existing, zone: newZone });
  const newInstances = Object.freeze({ ...state.instances, [instanceId]: updated });
  const newState: CardInstanceState = Object.freeze({ ...state, instances: newInstances });
  const nextLog = log.append({
    kind: "cardInstance.moveZone",
    data: { instanceId, from: existing.zone, to: newZone },
  });
  return { newState, log: nextLog };
}

/**
 * Transfer ownership of a CardInstance to a new seat.
 * Mirrors Unity TransferOwner(id, newSeat).
 */
export function transferOwner(
  state: CardInstanceState,
  log: ActionLog,
  instanceId: number,
  newSeat: number,
): TransferOwnerResult {
  assertFiniteInt(instanceId, "transferOwner instanceId");
  assertFiniteInt(newSeat, "transferOwner newSeat");
  const existing = state.instances[instanceId];
  if (existing === undefined) {
    console.error(`[CardInstanceService] TransferOwner: id ${instanceId} not found.`);
    throw new CardInstanceError(
      `[CardInstanceService] transferOwner: id ${instanceId} not found`,
      "NotFound",
      instanceId,
    );
  }
  const updated: CardInstance = Object.freeze({ ...existing, ownerSeat: newSeat });
  const newInstances = Object.freeze({ ...state.instances, [instanceId]: updated });
  const newState: CardInstanceState = Object.freeze({ ...state, instances: newInstances });
  const nextLog = log.append({
    kind: "cardInstance.transferOwner",
    data: { instanceId, from: existing.ownerSeat, to: newSeat },
  });
  return { newState, log: nextLog };
}

/**
 * Remove a CardInstance from state.
 * Logs warning (not error) when id is not present — mirrors Unity behavior.
 * Mirrors Unity Destroy(id).
 */
export function destroyInstance(
  state: CardInstanceState,
  log: ActionLog,
  instanceId: number,
): DestroyResult {
  assertFiniteInt(instanceId, "destroyInstance instanceId");
  if (state.instances[instanceId] === undefined) {
    console.warn(`[CardInstanceService] Destroy: id ${instanceId} not present.`);
    return { newState: state, log };
  }
  const newInstances: Record<string, CardInstance> = { ...state.instances };
  delete newInstances[instanceId];
  const newState: CardInstanceState = Object.freeze({
    ...state,
    instances: Object.freeze(newInstances),
  });
  const nextLog = log.append({
    kind: "cardInstance.destroy",
    data: { instanceId },
  });
  return { newState, log: nextLog };
}

/**
 * All instances owned by a given seat.
 * Mirrors Unity GetByOwner(seat).
 */
export function getByOwner(state: CardInstanceState, seat: number): ReadonlyArray<CardInstance> {
  assertFiniteInt(seat, "getByOwner seat");
  const result: CardInstance[] = [];
  for (const key of Object.keys(state.instances)) {
    const inst = state.instances[key];
    if (inst !== undefined && inst.ownerSeat === seat) {
      result.push(inst);
    }
  }
  return result;
}

/**
 * Count of all instances in state.
 * Mirrors Unity CardInstanceService.Count.
 */
export function countInstances(state: CardInstanceState): number {
  return Object.keys(state.instances).length;
}
